/**
 * Connect a new Amazon account: authorize Amazon, choose Shops, then finish.
 *
 * Onboarding and the in-app "连接 Amazon" dialog both render this, so the two
 * entries share one authorization and Shop-binding path and differ only in
 * how they close (first sync vs. "完成" back to Settings). The Amazon grants
 * (Ads, then Selling Partner) are complete before any Shop is bound, so Shop
 * selection is the step after authorization, not before it.
 */
import type { SpApiRegion } from "@adpilot/shared";
import { IconWorld } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AdsAccountCard } from "@/components/onboarding/ads-account-card";
import {
  AmazonConnectStep,
  type SellingPartnerOutcome,
} from "@/components/onboarding/amazon-connect-step";
import { EmptyState } from "@/components/shared/empty-state";
import { MarketplaceCard } from "@/components/shared/marketplace-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import type { AmazonAdsGrant } from "@/lib/amazon-ads-oauth";
import { fetchAmazonProfiles, selectAmazonProfiles } from "@/lib/amazon-api";
import { getApiErrorCode, getApiErrorMessage } from "@/lib/api-error";
import {
  selectAllProfileIds,
  selectedProfileIdsInOrder,
  toggleProfileId,
} from "@/lib/profile-selection";
import { cn } from "@/lib/utils";

type ConnectStep = "connect-amazon" | "select-profile" | "finish";

const STEP_ORDER: ConnectStep[] = ["connect-amazon", "select-profile", "finish"];

export type ActivatedShops = Awaited<ReturnType<typeof selectAmazonProfiles>>;

export interface AmazonConnectResult {
  shops: ActivatedShops["shops"];
  sellingPartner: SellingPartnerOutcome;
}

interface AmazonConnectFlowProps {
  /**
   * Runs once the selected Shops are bound, before the finish step shows —
   * refresh whatever the caller needs to see the new Shops.
   */
  onActivated: (shops: ActivatedShops) => Promise<void>;
  renderFinish: (result: AmazonConnectResult) => React.ReactNode;
}

/** Animated wrapper for each step — direction-aware horizontal slide + fade. */
function Step({
  children,
  direction,
  className,
}: {
  children: React.ReactNode;
  direction: "forward" | "backward" | "none";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-[320px] flex-col gap-7",
        direction === "forward" && "animate-step-forward",
        direction === "backward" && "animate-step-backward",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Left-aligned headline with optional decorative icon and description. */
export function StepHeader({
  icon: Icon,
  title,
  description,
}: {
  icon?: React.ReactNode;
  title: string;
  description?: string;
}) {
  return (
    <div>
      {Icon && (
        <div className="mb-4 flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          {Icon}
        </div>
      )}
      <h2 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h2>
      {description && <p className="mt-2 text-sm text-muted-foreground">{description}</p>}
    </div>
  );
}

export function AmazonConnectFlow({ onActivated, renderFinish }: AmazonConnectFlowProps) {
  const { t } = useTranslation("auth");

  const [step, setStep] = useState<ConnectStep>("connect-amazon");
  const [error, setError] = useState<string | null>(null);
  const [grantExpired, setGrantExpired] = useState(false);
  const [activating, setActivating] = useState(false);
  const [region, setRegion] = useState<SpApiRegion>("na");
  const [grant, setGrant] = useState<AmazonAdsGrant | null>(null);
  const [sellingPartner, setSellingPartner] = useState<SellingPartnerOutcome>("skipped");
  const [selectedProfileIds, setSelectedProfileIds] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<AmazonConnectResult | null>(null);

  // Direction-aware slide: animate only on an actual step change, never on the
  // initial mount. This is React's "store information from previous renders"
  // pattern — the setState calls run during render only when `step` changed,
  // re-rendering immediately without committing the stale intermediate.
  const [prevStep, setPrevStep] = useState<ConnectStep>(step);
  const [direction, setDirection] = useState<"forward" | "backward" | "none">("none");
  if (step !== prevStep) {
    setDirection(STEP_ORDER.indexOf(step) > STEP_ORDER.indexOf(prevStep) ? "forward" : "backward");
    setPrevStep(step);
  }

  // The popup relays Amazon's profiles only; which of them AdPraxis already
  // binds — and whether another Workspace owns them — is read back from the
  // grant cached on the server. Paused while activating, so the refresh that
  // follows a successful activation does not re-read the grant it consumed.
  const bindings = useQuery({
    queryKey: ["amazon", "profiles", grant?.selectionState],
    queryFn: () => fetchAmazonProfiles(grant?.selectionState ?? ""),
    enabled: grant !== null && step === "select-profile" && !activating,
    retry: false,
  });

  // Never substitute preview fixtures here: selection must be derived from
  // the exact profiles cached in the OAuth grant, or the CTA must stay off.
  const profiles = bindings.data?.profiles ?? grant?.profiles ?? [];
  // Binding these into this Workspace is always rejected, and one of them
  // fails the whole batch — so they are shown, but never selected.
  const takenIds = new Set(profiles.filter((p) => p.connectedByOther).map((p) => p.profileId));
  const selectableProfiles = profiles.filter((p) => !takenIds.has(p.profileId));
  const chosenIds = new Set([...selectedProfileIds].filter((id) => !takenIds.has(id)));
  const allChosen = selectableProfiles.length > 0 && chosenIds.size === selectableProfiles.length;

  function handleAdsGrant(data: AmazonAdsGrant) {
    setGrant(data);
    setSelectedProfileIds(selectAllProfileIds(data.profiles));
  }

  function retryAmazonAuthorization() {
    setGrant(null);
    setSelectedProfileIds(new Set());
    setError(null);
    setGrantExpired(false);
    setStep("connect-amazon");
  }

  const activateShops = useMutation({
    mutationFn: () =>
      selectAmazonProfiles({
        profileIds: selectedProfileIdsInOrder(profiles, chosenIds),
        state: grant?.selectionState ?? "",
      }),
    onMutate: () => {
      setActivating(true);
      setError(null);
      setGrantExpired(false);
    },
    onSettled: () => setActivating(false),
    onSuccess: async (data) => {
      await onActivated(data);
      setResult({ shops: data.shops, sellingPartner });
      setStep("finish");
    },
    // Shown inline next to the CTA rather than as a toast: the seller is looking
    // straight at the list the failure belongs to.
    onError: async (err) => {
      const [message, code] = await Promise.all([getApiErrorMessage(err), getApiErrorCode(err)]);
      setError(message);
      // A failed binding hands the grant back, so the seller can fix the
      // selection and retry; only an expired grant needs Amazon again.
      setGrantExpired(code === "amazon.state_expired");
      // Re-read bindings: the failure may be a profile another Workspace took
      // after this list loaded.
      if (code !== "amazon.state_expired") void bindings.refetch();
    },
  });

  return (
    <>
      {step === "connect-amazon" && (
        <Step key="connect-amazon" direction={direction}>
          <AmazonConnectStep
            region={region}
            onRegionChange={setRegion}
            grant={grant}
            onGrant={handleAdsGrant}
            onContinue={(outcome) => {
              setSellingPartner(outcome);
              setStep("select-profile");
            }}
          />
        </Step>
      )}

      {step === "select-profile" && (
        <Step key="select-profile" direction={direction}>
          {profiles.length === 0 ? (
            <EmptyState
              icon={<IconWorld className="size-8" />}
              title={t("selectProfile.emptyTitle")}
              description={t("selectProfile.emptyDescription")}
              actionLabel={t("selectProfile.tryAgain")}
              onAction={retryAmazonAuthorization}
            />
          ) : (
            <>
              <StepHeader
                icon={<IconWorld className="size-5" />}
                title={t("selectProfile.title")}
                description={t("selectProfile.found", { count: profiles.length })}
              />
              {profiles[0] ? (
                <AdsAccountCard name={profiles[0].name} email={grant?.accountEmail ?? null} />
              ) : null}
              <ScrollArea className="h-[220px]">
                <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
                  <div className="flex h-10 items-center justify-between border-line border-b bg-subtle px-3 text-[11px] leading-4 text-muted-foreground">
                    <span>
                      {t("selectProfile.selected", {
                        selected: chosenIds.size,
                        total: selectableProfiles.length,
                      })}
                    </span>
                    <button
                      type="button"
                      disabled={selectableProfiles.length === 0}
                      className="font-medium text-ink outline-none hover:underline focus-visible:underline disabled:opacity-50 disabled:hover:no-underline"
                      onClick={() =>
                        setSelectedProfileIds(
                          allChosen ? new Set() : selectAllProfileIds(selectableProfiles),
                        )
                      }
                    >
                      {allChosen ? t("selectProfile.clearAll") : t("selectProfile.selectAll")}
                    </button>
                  </div>
                  <div className="divide-y divide-line">
                    {profiles.map((profile, i) => {
                      const taken = takenIds.has(profile.profileId);
                      let status: React.ReactNode = null;
                      if (taken) {
                        status = (
                          <Badge variant="warning" size="compact">
                            {t("selectProfile.connectedElsewhere")}
                          </Badge>
                        );
                      } else if (profile.alreadyConnected) {
                        status = (
                          <Badge variant="neutral" size="compact">
                            {t("selectProfile.alreadyConnected")}
                          </Badge>
                        );
                      }
                      return (
                        <MarketplaceCard
                          key={profile.profileId}
                          profile={profile}
                          index={i}
                          selected={chosenIds.has(profile.profileId)}
                          disabled={taken}
                          status={status}
                          onSelectedChange={() =>
                            setSelectedProfileIds((selected) =>
                              toggleProfileId(selected, profile.profileId),
                            )
                          }
                        />
                      );
                    })}
                  </div>
                </div>
              </ScrollArea>
              {takenIds.size > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t("selectProfile.connectedElsewhereHint", { count: takenIds.size })}
                </p>
              ) : null}
              {error && <p className="text-center text-sm text-destructive-foreground">{error}</p>}
              {grantExpired ? (
                <Button type="button" variant="outline" onClick={retryAmazonAuthorization}>
                  {t("selectProfile.tryAgain")}
                </Button>
              ) : null}
              <Button
                type="button"
                // Wait for the binding flags, so a taken profile can never be
                // submitted with the rest; if they fail to load, the server
                // still rejects a taken profile on its own.
                disabled={activateShops.isPending || bindings.isPending || chosenIds.size === 0}
                onClick={() => {
                  if (chosenIds.size > 0) activateShops.mutate();
                }}
                className="w-full"
              >
                {activateShops.isPending ? <Spinner className="size-4" /> : null}
                {activateShops.isPending
                  ? t("selectProfile.connecting")
                  : t("selectProfile.activateSelected", { count: chosenIds.size })}
              </Button>
            </>
          )}
        </Step>
      )}

      {step === "finish" && result && (
        <Step key="finish" direction={direction}>
          {renderFinish(result)}
        </Step>
      )}
    </>
  );
}
